import React, { FC, useMemo } from 'react'
import './index.less'
import { computeDetection, DetectionStatus, DetectionTag, ExaminationValueTag, EyeVals } from '../helpers'
import { DecryptedService } from '@icure/cardinal-sdk'
import { Image, Skeleton } from 'antd'
import { useTranslation } from 'react-i18next'
import { getImgSRC } from '../../../../../../helpers/fileToBase64'
import { useGetAndTryDecryptMainAttachmentQuery } from '../../../../../../core/api/documentApi'
import { skipToken } from '@reduxjs/toolkit/query'
import { CameraFilled } from '@ant-design/icons'

interface ConsultationContentProps {
  diabeticMaculopathyExaminationValue: { dexter: EyeVals; sinister: EyeVals }
  diabeticRetinopathyExaminationValue: { dexter: EyeVals; sinister: EyeVals }
  oculusSinisterService: DecryptedService | undefined
  oculusDexterService: DecryptedService | undefined
  notesService: DecryptedService | undefined
  diagnosisService: DecryptedService | undefined
}

export const ConsultationContent: FC<ConsultationContentProps> = ({
  diabeticMaculopathyExaminationValue,
  diabeticRetinopathyExaminationValue,
  oculusSinisterService,
  oculusDexterService,
  notesService,
  diagnosisService,
}) => {
  const { t } = useTranslation()
  const oculusSinisterDocumentId = oculusSinisterService?.content?.documentId.documentId
  const oculusDexterDocumentId = oculusDexterService?.content?.documentId.documentId

  const { data: decryptedOculusSinisterDocument, isFetching: isDecryptedOculusSinisterDocumentLoading } = useGetAndTryDecryptMainAttachmentQuery(
    oculusSinisterDocumentId ?? skipToken,
  )
  const { data: decryptedOculusDexterDocument, isFetching: isDecryptedOculusDexterDocumentLoading } = useGetAndTryDecryptMainAttachmentQuery(oculusDexterDocumentId ?? skipToken)
  const oculusSinisterPictureSrc = getImgSRC(decryptedOculusSinisterDocument)
  const oculusDexterPictureSrc = getImgSRC(decryptedOculusDexterDocument)

  const maculopathyStatus = useMemo(() => computeDetection(diabeticMaculopathyExaminationValue), [diabeticMaculopathyExaminationValue])
  const retinopathyStatus = useMemo(() => computeDetection(diabeticRetinopathyExaminationValue), [diabeticRetinopathyExaminationValue])

  const getExaminationResultsExplanations = (type: 'ret' | 'mac', status: DetectionStatus) => {
    const examinationType = type === 'ret' ? t('consultations.types.retinopathy') : t('consultations.types.maculopathy')
    const examinationTypeValues = type === 'ret' ? diabeticRetinopathyExaminationValue : diabeticMaculopathyExaminationValue
    const dexter = examinationTypeValues.dexter.value?.toFixed(3) ?? '-'
    const sinister = examinationTypeValues.sinister.value?.toFixed(3) ?? '-'
    const referral = examinationTypeValues.dexter.ref ?? examinationTypeValues.sinister.ref ?? '-'

    const detectedTitle = t('consultations.referralDetectedTitle', { type: examinationType })
    const undetectedTitle = t('consultations.referralUndetectedTitle', { type: examinationType })

    const detectedSubtitle = t('consultations.referralDetectedSubtitle', {
      dexter,
      sinister,
      referal: referral,
      type: examinationType,
    })
    const undetectedSubtitle = t('consultations.referralUndetectedSubtitle', {
      dexter,
      sinister,
      referal: referral,
      type: examinationType,
    })

    return {
      title: status === 'detected' ? detectedTitle : status === 'undetected' ? undetectedTitle : `-`,
      subtitle: status === 'detected' ? detectedSubtitle : status === 'undetected' ? undetectedSubtitle : `-`,
    }
  }

  const notes = notesService?.content?.descr?.stringValue || '—'
  const diagnosis = diagnosisService?.content?.descr?.stringValue || '—'

  const getImageSkeleton = () => {
    return (
      <div className="eyesScans__item__scanPlaceholder">
        <Skeleton.Node active={true}>
          <CameraFilled style={{ fontSize: 40, color: '#094e75' }} />
        </Skeleton.Node>
      </div>
    )
  }
  const getImagePlaceholder = () => {
    return (
      <div className="eyesScans__item__scanPlaceholder">
        <CameraFilled style={{ fontSize: 40, color: '#094e75' }} />
        <p>{t('consultations.imageMissing')}</p>
      </div>
    )
  }

  return (
    <div className="consultationContent">
      <div className="consultationContent__item consultationContent__item--examenationValues rethinopathy">
        <div className="consultationContent__item--examenationValues__header">
          <DetectionTag status={retinopathyStatus} />
          <h4>{t('consultations.types.retinopathy')}</h4>
        </div>
        <div className="consultationContent__item--examenationValues__content">
          <h5>{getExaminationResultsExplanations('ret', retinopathyStatus).title}</h5>
          <p>{getExaminationResultsExplanations('ret', retinopathyStatus).subtitle}</p>
        </div>
      </div>
      <div className="consultationContent__item consultationContent__item--examenationValues maculopathy">
        <div className="consultationContent__item--examenationValues__header">
          <DetectionTag status={maculopathyStatus} />
          <h4>{t('consultations.types.maculopathy')}</h4>
        </div>
        <div className="consultationContent__item--examenationValues__content">
          <h5>{getExaminationResultsExplanations('mac', maculopathyStatus).title}</h5>
          <p>{getExaminationResultsExplanations('mac', maculopathyStatus).subtitle}</p>
        </div>
      </div>

      <div className="consultationContent__item eyesScans">
        <div className="eyesScans__item rigthEye">
          <h4>{t('consultations.eye.right')}</h4>
          <div className="eyesScans__item__heading">
            <div className="eyesScans__item__heading__item">
              <p>{t('consultations.types.retinopathy')}:</p>
              <ExaminationValueTag values={diabeticRetinopathyExaminationValue.dexter} side={'dexter'} />
            </div>
            <div className="eyesScans__item__heading__item">
              <p>{t('consultations.types.maculopathy')}:</p>
              <ExaminationValueTag values={diabeticMaculopathyExaminationValue.dexter} side={'dexter'} />
            </div>
          </div>

          {isDecryptedOculusDexterDocumentLoading ? (
            getImageSkeleton()
          ) : !!oculusDexterPictureSrc ? (
            <div className="eyesScans__item__scanImage">
              <Image src={oculusDexterPictureSrc} />
            </div>
          ) : (
            getImagePlaceholder()
          )}
        </div>
        <div className="eyesScans__item leftEye">
          <h4>{t('consultations.eye.left')}</h4>
          <div className="eyesScans__item__heading">
            <div className="eyesScans__item__heading__item">
              <p>{t('consultations.types.retinopathy')}:</p>
              <ExaminationValueTag values={diabeticRetinopathyExaminationValue.sinister} side={'sinister'} />
            </div>
            <div className="eyesScans__item__heading__item">
              <p>{t('consultations.types.maculopathy')}:</p>
              <ExaminationValueTag values={diabeticMaculopathyExaminationValue.sinister} side={'sinister'} />
            </div>
          </div>

          {isDecryptedOculusSinisterDocumentLoading ? (
            getImageSkeleton()
          ) : !!oculusSinisterPictureSrc ? (
            <div className="eyesScans__item__scanImage">
              <Image src={oculusSinisterPictureSrc} />
            </div>
          ) : (
            getImagePlaceholder()
          )}
        </div>
        <div className="eyesScans__item notes">
          <h4>{t('consultations.notesTitle')}</h4>
          <p>{notes}</p>
        </div>
        <div className="eyesScans__item diagnosis">
          <h4>{t('consultations.diagnosisTitle')}</h4>
          <p>{diagnosis}</p>
        </div>
      </div>
    </div>
  )
}
